import React, {ErrorInfo, Key, PropsWithChildren, PureComponent} from 'react';

import {reportToBugsnag} from '../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {ErrorFallback, ErrorUserProps} from './errorFallback';

/*
 * Props.
 */

interface ErrorReporterProps extends ErrorUserProps {
  /** Change the value of this prop to clear the error state. */
  errorKey?: Key;
  isTopLevel: boolean;
}

type ErrorReporterPropsWithChildren = PropsWithChildren<ErrorReporterProps>;

/*
 * Component.
 */

interface ErrorReporterState {
  prevErrorKey?: Key;
  error?: any;
}

export class ErrorReporter extends PureComponent<ErrorReporterPropsWithChildren, ErrorReporterState> {
  constructor(props: ErrorReporterPropsWithChildren) {
    super(props);
    this.state = {
      prevErrorKey: props.errorKey,
    };
  }

  static getDerivedStateFromProps(
    nextProps: Readonly<ErrorReporterPropsWithChildren>,
    state: ErrorReporterState,
  ): Partial<ErrorReporterState> | null {
    if (state.prevErrorKey === nextProps.errorKey) {
      return null;
    }

    return {
      error: undefined,
      prevErrorKey: nextProps.errorKey,
    };
  }

  static getDerivedStateFromError(error: any) {
    return {error};
  }

  componentDidCatch(error: any, errorInfo: ErrorInfo) {
    reportToBugsnag(error, {
      metadata: {
        react: {componentStack: formatComponentStack(errorInfo.componentStack || '')},
        front: {
          didTriggerWhiteScreen: false,
          level: 'render',
        },
      },
    });
  }

  render() {
    if (this.state.error) {
      return (
        <ErrorFallback
          companyId={this.props.companyId}
          userId={this.props.userId}
          error={this.state.error}
          onRetryClick={() => this.setState({error: undefined})}
          isTopLevel={this.props.isTopLevel}
        />
      );
    }

    return this.props.children;
  }
}

/*
 * Helpers.
 */

function formatComponentStack(str: string) {
  const lines = str.split(/\s*\n\s*/g);
  let ret = '';
  for (let line = 0, len = lines.length; line < len; line++) {
    if (lines[line].length) {
      ret += `${ret.length ? '\n' : ''}${lines[line]}`;
    }
  }
  return ret;
}
